<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import AudienceLinks from '../components/AudienceLinks.vue'
import AudienceTeaser from '../components/AudienceTeaser.vue'
import BrandMark from '../components/BrandMark.vue'
import LandingFooter from '../components/LandingFooter.vue'
import PictureMarquee from '../components/PictureMarquee.vue'
import { useReveal } from '../composables/useReveal'

const page = ref(null)
useReveal(page)
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Hero: the AI opponent describes the player's picture and gets its own version.
const duels = [
  { original: '/assets/company/iceberg-original.webp', replica: '/assets/company/iceberg-replica.webp', alt: 'Ракета-унитаз несётся по штормовому морю мимо айсберга', replicaAlt: 'Версия ИИ: белый унитаз парит у ледяной глыбы, вдали круизное судно', answer: 'Белая сантехника парит рядом с огромной ледяной глыбой, вдали круизное судно', score: 52 },
  { original: '/assets/original.webp', replica: '/assets/replica.webp', alt: 'Рыжий котёнок-космонавт на сиреневой Луне', replicaAlt: 'Версия ИИ: серый котёнок в скафандре на синей каменистой равнине', answer: 'Пушистый малыш в белом защитном костюме со стеклянным шлемом сидит на каменистой равнине среди звёзд', score: 88 },
]
const duelIndex = ref(0)
const duel = computed(() => duels[duelIndex.value])
const typed = ref(duels[0].answer)
const phase = ref('shown')
let heroRun = 0
let heroTimer
const heroWait = ms => new Promise(resolve => { heroTimer = window.setTimeout(resolve, ms) })
async function playDuels() {
  const token = ++heroRun
  while (token === heroRun) {
    phase.value = 'looking'
    typed.value = ''
    await heroWait(1300)
    if (token !== heroRun) return
    phase.value = 'typing'
    for (const char of duel.value.answer) {
      await heroWait(38)
      if (token !== heroRun) return
      typed.value += char
    }
    phase.value = 'drawing'
    await heroWait(1300)
    if (token !== heroRun) return
    phase.value = 'shown'
    await heroWait(4200)
    if (token !== heroRun) return
    duelIndex.value = (duelIndex.value + 1) % duels.length
  }
}

const topRow = [
  { src: '/assets/company/iceberg-original.webp', alt: 'Ракета-унитаз в штормовом море', label: 'Морская ракета' },
  { src: '/assets/kids/catstronaut-original.webp', alt: 'Кот в скафандре на фоне луны', label: 'Кот на орбите' },
  { src: '/assets/company/burger-original.webp', alt: 'Бургер парит над городом', label: 'Бургер над городом' },
  { src: '/assets/kids/banana-space.webp', alt: 'Бананы на летающей тарелке', label: 'Бананы в космосе' },
  { src: '/assets/company/caveman-original.webp', alt: 'Пиксельный великан у горы', label: 'Пиксельный великан' },
  { src: '/assets/kids/dumplings-original.webp', alt: 'Кот лепит пельмени', label: 'Кот-повар' },
  { src: '/assets/company/whale-original.webp', alt: 'Кит над ночным городом и салют', label: 'Кит на празднике' },
]
const bottomRow = [
  { src: '/assets/company/lava-original.webp', alt: 'Металлический шар на лаве', label: 'Шар на лаве' },
  { src: '/assets/kids/toycow-original.webp', alt: 'Мальчик на корове-качалке в лесу', label: 'Лесная качалка' },
  { src: '/assets/company/pumpkin-comic.webp', alt: 'Тыква и удивлённый парень в стиле комикса', label: 'Спор с тыквой' },
  { src: '/assets/kids/bike-dream.webp', alt: 'Мальчик на велосипеде в сказочном городе', label: 'Сказочный город' },
  { src: '/assets/company/mermaid-original.webp', alt: 'Русалка в бокале', label: 'Русалка в бокале' },
  { src: '/assets/kids/teapot-original.webp', alt: 'Красный чайник на голубом фоне', label: 'Красный чайник' },
  { src: '/assets/company/pig-original.webp', alt: 'Свинья верхом на коне', label: 'Свинья-наездница' },
]

const rounds = [
  {
    tab: 'Раунд 1', role: 'загадываете вы',
    author: 'Вы загадали', original: '/assets/company/iceberg-original.webp', originalAlt: 'Оригинал: ракета-унитаз несётся по штормовому морю мимо айсберга, вдали старый лайнер',
    prompt: 'Ракета-унитаз несётся по штормовому морю мимо айсберга, вдалеке старый лайнер', forbidden: ['ракета', 'унитаз', 'море', 'айсберг', 'лайнер'],
    responder: 'ИИ ответил', replica: '/assets/company/iceberg-replica.webp', replicaAlt: 'Версия ИИ: белый унитаз парит у ледяной глыбы в спокойной воде, вдали круизное судно',
    answer: 'Белая сантехника парит рядом с огромной ледяной глыбой, вдали круизное судно', score: 52, note: 'Шторм и ракетный корпус ИИ не заметил — вот и потерял баллы',
  },
  {
    tab: 'Раунд 2', role: 'загадывает ИИ',
    author: 'ИИ загадал', original: '/assets/company/lava-original.webp', originalAlt: 'Оригинал ИИ: хромированный шар лежит на раскалённой лаве',
    prompt: 'Хромированный шар лежит на раскалённой лаве', forbidden: ['шар', 'лава'],
    responder: 'Вы ответили', replica: '/assets/company/lava-replica.webp', replicaAlt: 'Ваша версия: металлическая шайба на огненной поверхности',
    answer: 'Круглая блестящая металлическая штука на огненной пузырящейся поверхности', score: 64, note: '«Круглая» вышла плоской: шайба вместо шара. Но огонь и металл на месте',
  },
]
const activeRound = ref(0)
const round = computed(() => rounds[activeRound.value])

// «Как думает ИИ»: forbidden words get replaced by descriptions, one by one.
const mindParts = [
  { text: 'Рыжий ' }, { from: 'кот', to: 'пушистый зверёк' }, { text: ' в бело-оранжевом ' }, { from: 'скафандре', to: 'костюме со стеклянным шлемом' },
  { text: ' сидит на ' }, { from: 'Луне', to: 'сиреневой каменистой равнине' }, { text: ', в небе планета с кольцом' },
]
const swaps = mindParts.filter(part => part.from).length
const mindSteps = [
  { title: 'Рассматривает картинку', text: 'Видит то же, что и вы: героя, фон, цвета и расположение предметов.' },
  { title: 'Находит запреты', text: 'Главные слова и их формы под запретом — «котик» и «лунный» тоже не пройдут.' },
  { title: 'Заменяет признаками', text: 'Вместо названия — внешность, материал, место и цвет.' },
  { title: 'Отправляет описание', text: 'По нему рисуется версия ИИ, а потом её сравнивают с оригиналом.' },
]
// The prerendered page shows the finished answer; the animation starts once the block is on screen.
const mindStep = ref(3)
const swapped = ref(swaps)
const mindBlock = ref(null)
let mindRun = 0
let mindTimer
let mindObserver
const mindWait = ms => new Promise(resolve => { mindTimer = window.setTimeout(resolve, ms) })
const mindTokens = computed(() => {
  let index = 0
  return mindParts.map(part => {
    if (!part.from) return { text: part.text }
    const order = index++
    if (mindStep.value >= 2 && swapped.value > order) return { text: part.to, state: 'replaced' }
    return { text: part.from, state: mindStep.value >= 1 ? 'forbidden' : 'plain' }
  })
})
async function playMind() {
  if (reducedMotion()) return
  const token = ++mindRun
  mindStep.value = 0
  swapped.value = 0
  for (const [step, delay] of [[1, 1500], [2, 1500]]) {
    await mindWait(delay)
    if (token !== mindRun) return
    mindStep.value = step
  }
  while (swapped.value < swaps) {
    await mindWait(1000)
    if (token !== mindRun) return
    swapped.value++
  }
  await mindWait(900)
  if (token === mindRun) mindStep.value = 3
}

onMounted(() => {
  if (!reducedMotion()) playDuels()
  if (!('IntersectionObserver' in window) || !mindBlock.value) return
  mindObserver = new IntersectionObserver(entries => {
    if (!entries.some(entry => entry.isIntersecting)) return
    mindObserver.disconnect()
    playMind()
  }, { threshold: 0.45 })
  mindObserver.observe(mindBlock.value)
})
onBeforeUnmount(() => {
  heroRun++
  mindRun++
  window.clearTimeout(heroTimer)
  window.clearTimeout(mindTimer)
  mindObserver?.disconnect()
})

const perks = [
  { icon: '⚡', title: 'Старт без ожидания', text: 'Не нужно собирать компанию и ждать, пока все подключатся. Матч начинается сразу после создания.' },
  { icon: '🌙', title: 'Когда удобно', text: 'Ночью, в дороге или в обеденный перерыв. ИИ не устаёт и никогда не говорит «давай завтра».' },
  { icon: '⚖️', title: 'Честный соперник', text: 'ИИ видит только картинку и запретные слова — ваш текст ему недоступен. Оценка одна для обоих.' },
  { icon: '✍️', title: 'Тренажёр промптов', text: 'Учитесь описывать сцену так, чтобы нейросеть поняла с первого раза. Пригодится и в других генераторах картинок.' },
  { icon: '🧠', title: 'Разминка для мозга', text: 'Подобрать синоним, заметить деталь, описать композицию — короткая зарядка для словарного запаса.' },
  { icon: '🏆', title: 'Форма перед вечеринкой', text: 'Потренируйтесь один на один, а потом удивите друзей точностью описаний в общей игре.' },
]

const criteria = [
  { name: 'Объекты', weight: 40, tip: 'Все герои и предметы на месте — назовите их признаками, а не запретными словами.' },
  { name: 'Композиция', weight: 25, tip: 'Что в центре, что слева, что на заднем плане, какой ракурс.' },
  { name: 'Палитра', weight: 20, tip: 'Цвета, свет, время суток и погода — шторм или штиль.' },
  { name: 'Стиль', weight: 15, tip: 'Фотография, мультфильм, акварель или пиксель-арт.' },
]

const steps = [
  { title: 'Выберите режим', text: 'На странице создания игры отметьте «Один на один с ИИ» и впишите своё имя.' },
  { title: 'Задайте стиль', text: 'Аниме, комикс, акварель, старое кино или пиксель-арт — он применится ко всем картинкам матча.' },
  { title: 'Загадайте сцену', text: 'Первый ход ваш. Чем неожиданнее идея, тем сложнее ИИ её повторить.' },
]

const openQuestion = ref(null)
const questions = [
  { q: 'Как устроен матч с ИИ?', a: 'Два раунда. В первом вы загадываете сцену, и ИИ пытается её повторить, не называя главного. Во втором ИИ сам придумывает сцену, а повторяете вы. Побеждает тот, чья версия получила больше баллов.' },
  { q: 'ИИ знает, что я написал?', a: 'Нет. Соперник видит только готовую картинку и список запретных слов — ровно как вы в его раунде. Ваш текст раскрывается только в итогах.' },
  { q: 'Сколько это стоит?', a: 'Одна игра в день бесплатна — её хватает на целый матч с ИИ. Больше игр можно взять по подписке или пакетом.' },
  { q: 'Можно показать игру друзьям?', a: 'Да. Отправьте ссылку на комнату: друзья подключатся зрителями и будут болеть за вас. Играют в матче только двое — вы и ИИ.' },
  { q: 'Что если я закрою вкладку?', a: 'Ничего не пропадёт. ИИ ходит на сервере, а ваш ход и статус генерации сохраняются. Вернитесь в игру через раздел «Мои игры».' },
  { q: 'ИИ иногда ошибается?', a: 'Конечно — и в этом половина удовольствия. Он может не заметить шторм, перепутать шар с шайбой или увидеть в тыкве кота. Ваша задача — загадать так, чтобы он ошибся.' },
]
</script>

<template>
  <main ref="page" class="audience-page solo-page landing-page">
    <section class="audience-hero" aria-labelledby="solo-title">
      <div class="audience-hero-copy">
        <span class="round-overline">Для одного игрока</span>
        <h1 id="solo-title">Не с кем играть? <span>Обыграйте ИИ</span></h1>
        <p>ИИ-соперник всегда готов к матчу. Вы загадываете сцену — он пытается повторить её, не называя главного. Потом загадывает он, и теперь ваша очередь. Кто точнее объяснит картинку, тот и победил.</p>
        <div class="landing-hero-actions"><RouterLink class="button button-primary" :to="{ path: '/create', query: { mode: 'ai' } }">Играть с ИИ <span aria-hidden="true">→</span></RouterLink><a class="button button-outline" href="#match">Как проходит матч</a></div>
        <ul class="audience-facts"><li>старт без ожидания</li><li>в любое время суток</li><li>одна игра в день бесплатно</li></ul>
      </div>
      <div class="solo-duel">
        <div class="solo-duel-blob" aria-hidden="true" />
        <div class="solo-scoreboard" aria-hidden="true"><span class="solo-avatar">Вы</span><b>Матч · раунд 1 из 2</b><span class="solo-avatar solo-avatar-ai">ИИ</span></div>
        <figure class="solo-duel-card solo-duel-original"><img :key="duel.original" fetchpriority="high" :src="duel.original" :alt="`Ваша загадка: ${duel.alt}`" /><figcaption>Ваша загадка</figcaption></figure>
        <span class="solo-vs" aria-hidden="true">VS</span>
        <figure class="solo-duel-card solo-duel-replica" :class="`is-${phase}`">
          <img :key="duel.replica" :src="duel.replica" :alt="duel.replicaAlt" />
          <span v-if="phase === 'looking'" class="solo-duel-status">ИИ рассматривает…</span>
          <span v-else-if="phase === 'typing'" class="solo-duel-status">ИИ подбирает слова…</span>
          <span v-else-if="phase === 'drawing'" class="solo-duel-status">Рисуем версию ИИ…</span>
          <b v-else class="solo-duel-score">{{ duel.score }}<small>/100</small></b>
          <figcaption>Версия ИИ</figcaption>
        </figure>
        <div class="solo-ai-bubble"><span><i aria-hidden="true">🤖</i> ИИ-соперник описывает:</span><p>{{ typed }}<i v-if="phase === 'typing'" class="kids-caret" aria-hidden="true" /><em v-if="phase === 'looking'" class="solo-thinking" aria-hidden="true"><i /><i /><i /></em></p></div>
      </div>
    </section>

    <AudienceTeaser current="solo" />

    <section class="audience-marquee" aria-label="Картинки из настоящих игр">
      <PictureMarquee :items="topRow" :duration="72" />
      <PictureMarquee :items="bottomRow" :duration="82" reverse />
    </section>

    <section id="match" class="solo-match" aria-labelledby="match-title">
      <div class="audience-heading" data-reveal><span class="round-overline">Матч крупным планом</span><h2 id="match-title">Два раунда: сначала загадываете вы, потом — ИИ</h2><p>Переключайте раунды и смотрите, кто кого перехитрил.</p></div>
      <div class="solo-match-board" data-reveal>
        <div class="solo-match-tabs" role="group" aria-label="Раунды матча">
          <button v-for="(item, index) in rounds" :key="item.tab" type="button" :class="{ 'is-active': activeRound === index }" :aria-pressed="activeRound === index" @click="activeRound = index"><b>{{ item.tab }}</b><span>{{ item.role }}</span></button>
          <button type="button" :class="{ 'is-active': activeRound === rounds.length }" :aria-pressed="activeRound === rounds.length" @click="activeRound = rounds.length"><b>Итог</b><span>кто победил</span></button>
        </div>
        <Transition name="audience-swap" mode="out-in">
          <div v-if="round" :key="activeRound" class="solo-match-round">
            <div class="solo-match-side">
              <figure><img :src="round.original" :alt="round.originalAlt" loading="lazy" /><figcaption>Оригинал</figcaption></figure>
              <div class="solo-match-note"><span>{{ round.author }}:</span><p>«{{ round.prompt }}»</p><small>Нельзя писать:</small><div class="round-forbidden"><span v-for="word in round.forbidden" :key="word">{{ word }}</span></div></div>
            </div>
            <span class="solo-match-arrow" aria-hidden="true">→</span>
            <div class="solo-match-side solo-match-answer">
              <figure><img :src="round.replica" :alt="round.replicaAlt" loading="lazy" /><figcaption>Повтор</figcaption></figure>
              <div class="solo-match-note"><span>{{ round.responder }}:</span><p>«{{ round.answer }}»</p><div class="company-score"><strong>{{ round.score }}<small>/100</small></strong><div class="company-score-track"><i :style="{ width: `${round.score}%` }" /></div></div><small>{{ round.note }}</small></div>
            </div>
          </div>
          <div v-else key="final" class="solo-match-final">
            <div class="solo-final-score"><span><b class="solo-avatar">Вы</b>64</span><i>:</i><span>52<b class="solo-avatar solo-avatar-ai">ИИ</b></span></div>
            <h3>Победа за вами! 🎉</h3>
            <p>ИИ не заметил шторм и ракету, а вы превратили шар в шайбу. Ваша ошибка обошлась дешевле — 64 против 52.</p>
            <div class="solo-final-thumbs"><img v-for="item in rounds" :key="item.replica" :src="item.replica" :alt="item.replicaAlt" loading="lazy" /></div>
            <RouterLink class="button button-primary" :to="{ path: '/create', query: { mode: 'ai' } }">Сыграть свой матч →</RouterLink>
          </div>
        </Transition>
      </div>
      <p class="company-round-note">Картинки — из настоящих игр. Описания и баллы — пример того, как проходит матч.</p>
    </section>

    <section id="ai-mind" class="solo-mind" aria-labelledby="mind-title">
      <div class="solo-mind-copy" data-reveal>
        <span class="round-overline">Подсмотрите приёмы соперника</span>
        <h2 id="mind-title">Как ИИ обходит запретные слова</h2>
        <p>ИИ играет по тем же правилам, что и вы: видит картинку, знает запреты и ищет обходные пути. Посмотрите, как он это делает, — и используйте против него.</p>
        <div class="kids-trainer-picture"><img src="/assets/original.webp" alt="Рыжий котёнок в скафандре сидит на сиреневой Луне, в небе планета с кольцом" loading="lazy" /><div class="guide-banned"><s>кот</s><s>луна</s><s>скафандр</s></div></div>
      </div>
      <div ref="mindBlock" class="solo-mind-panel" data-reveal style="--reveal-delay: .1s">
        <ol class="solo-mind-steps">
          <li v-for="(item, index) in mindSteps" :key="item.title" :class="{ 'is-done': mindStep > index, 'is-active': mindStep === index }"><b>{{ index + 1 }}</b><span><strong>{{ item.title }}</strong>{{ item.text }}</span></li>
        </ol>
        <div class="solo-mind-text" :class="{ 'is-ready': mindStep === 3 }">
          <span><i aria-hidden="true">🤖</i> Описание ИИ</span>
          <p><template v-for="(token, index) in mindTokens" :key="index"><mark v-if="token.state" :class="`is-${token.state}`">{{ token.text }}</mark><template v-else>{{ token.text }}</template></template>.</p>
        </div>
        <button v-if="mindStep === 3" type="button" class="text-button" @click="playMind">↻ Показать ещё раз</button>
      </div>
    </section>

    <section class="solo-perks" aria-labelledby="perks-title">
      <div class="audience-heading" data-reveal><span class="round-overline">Зачем играть с ИИ</span><h2 id="perks-title">Соперник, который всегда в настроении</h2></div>
      <div class="company-occasion-grid">
        <article v-for="(item, index) in perks" :key="item.title" data-reveal :style="{ '--reveal-delay': `${(index % 3) * 0.08}s` }"><span aria-hidden="true">{{ item.icon }}</span><h3>{{ item.title }}</h3><p>{{ item.text }}</p></article>
      </div>
    </section>

    <section class="solo-criteria" aria-labelledby="criteria-title">
      <div class="company-why-copy" data-reveal>
        <span class="round-overline">Как набрать больше баллов</span>
        <h2 id="criteria-title">ИИ судит по четырём критериям</h2>
        <p>Картинки сравниваются по объектам, композиции, палитре и стилю. После каждого раунда видно, где вы потеряли баллы, — в следующем матче закрывайте слабые места.</p>
        <RouterLink class="button button-outline" to="/how-to">Подробные правила →</RouterLink>
      </div>
      <div class="solo-criteria-list">
        <article v-for="(item, index) in criteria" :key="item.name" data-reveal :style="{ '--reveal-delay': `${index * 0.08}s` }">
          <div><strong>{{ item.name }}</strong><b>{{ item.weight }}%</b></div>
          <div class="company-score-track"><i :style="{ width: `${item.weight * 2.5}%` }" /></div>
          <p>{{ item.tip }}</p>
        </article>
      </div>
    </section>

    <section class="solo-start" aria-labelledby="solo-start-title">
      <div class="audience-heading" data-reveal><span class="round-overline">Как начать</span><h2 id="solo-start-title">Три шага до первого раунда</h2></div>
      <ol class="solo-start-list">
        <li v-for="(item, index) in steps" :key="item.title" data-reveal :style="{ '--reveal-delay': `${index * 0.08}s` }"><b>{{ index + 1 }}</b><h3>{{ item.title }}</h3><p>{{ item.text }}</p></li>
      </ol>
    </section>

    <section class="guide-faq" aria-labelledby="solo-faq-title">
      <h2 id="solo-faq-title">Вопросы об игре с ИИ</h2>
      <div><article v-for="(item, index) in questions" :key="item.q"><button type="button" :aria-expanded="openQuestion === index" @click="openQuestion = openQuestion === index ? null : index">{{ item.q }}<span aria-hidden="true">{{ openQuestion === index ? '−' : '+' }}</span></button><Transition name="guide-answer"><p v-show="openQuestion === index">{{ item.a }}</p></Transition></article></div>
    </section>

    <AudienceLinks current="solo" title="Другие способы играть" title-id="more-audiences-title" />
    <section class="landing-cta" aria-labelledby="solo-cta-title"><div><BrandMark /><h2 id="solo-cta-title">ИИ уже размялся. Ваш ход</h2></div><div><RouterLink class="button cta-light" :to="{ path: '/create', query: { mode: 'ai' } }">Играть с ИИ</RouterLink><RouterLink class="button cta-outline" to="/how-to">Как играть</RouterLink></div></section>
    <LandingFooter />
  </main>
</template>
